/**
 * Money helpers. Amounts sind als String-Decimals (de: "1234.56") über die
 * API unterwegs, im UI rechnen wir via dinero.js in Minor-Units.
 */

export type Currency = "EUR" | "USD" | "CHF" | "GBP";

export interface Money {
  amount: string; // decimal-string
  currency: Currency;
}

const MINORS: Record<Currency, number> = { EUR: 2, USD: 2, CHF: 2, GBP: 2 };

export function toMinor(amount: string | number, currency: Currency): number {
  const factor = 10 ** MINORS[currency];
  const n = typeof amount === "string" ? parseFloat(amount.replace(",", ".")) : amount;
  if (Number.isNaN(n)) return 0;
  return Math.round(n * factor);
}

export function fromMinor(minor: number, currency: Currency): string {
  const factor = 10 ** MINORS[currency];
  return (minor / factor).toFixed(MINORS[currency]);
}

export function formatMoney(money: Money | undefined | null, locale = "de-DE"): string {
  if (!money) return "—";
  const n = typeof money.amount === "number" ? money.amount : parseFloat(money.amount.replace(",", "."));
  if (Number.isNaN(n)) return "—";
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency: money.currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n);
}

export function formatAmountOnly(amount: string | number, locale = "de-DE"): string {
  const n = typeof amount === "number" ? amount : parseFloat(amount);
  if (Number.isNaN(n)) return "—";
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n);
}

export function parseMoney(input: string, currency: Currency = "EUR"): Money {
  const cleaned = input
    .replace(/[^0-9,.\-]/g, "")
    .replace(/\./g, "")
    .replace(",", ".");
  const n = parseFloat(cleaned);
  if (Number.isNaN(n)) return { amount: "0.00", currency };
  return { amount: n.toFixed(2), currency };
}

export function zero(currency: Currency = "EUR"): Money {
  return { amount: "0.00", currency };
}

export function sum(values: Money[], currency: Currency = "EUR"): Money {
  const total = values
    .filter((m) => m.currency === currency)
    .reduce((acc, m) => acc + toMinor(m.amount, currency), 0);
  return { amount: fromMinor(total, currency), currency };
}

export function cmp(a: Money, b: Money): number {
  if (a.currency !== b.currency) return a.currency.localeCompare(b.currency);
  return toMinor(a.amount, a.currency) - toMinor(b.amount, b.currency);
}

export function formatIban(iban?: string | null): string {
  if (!iban) return "—";
  return iban
    .replace(/\s+/g, "")
    .toUpperCase()
    .replace(/(.{4})/g, "$1 ")
    .trim();
}

export function maskIban(iban?: string | null): string {
  if (!iban) return "—";
  const clean = iban.replace(/\s+/g, "").toUpperCase();
  if (clean.length < 8) return clean;
  return clean.slice(0, 4) + " •••• •••• " + clean.slice(-4);
}
